import React, { useCallback, useEffect, useState } from 'react';
import * as Styled from './styled';
import { TextField } from 'ds/components/input/TextField';
import { useLocale } from 'locale';
import SuccessIcon from 'assets/svg/success-ic.svg';
import ProfileIcon from 'assets/svg/profile.svg';
import PhoneIconNegative from 'assets/svg/phone.svg';
import EmailIcon from 'assets/svg/mail-ic.svg';
import { Button } from 'ds/components/button';
import { isEmailValid, isPhoneNumberValid } from 'utils/formValidationHelpers';
import { useScreenBreakpoint } from 'consts/breakpoints';


export enum FormFieldMode {
  Row = 'row',
  Column = 'column',
}

export interface ContactFormSubmitData {
  userName: string;
  userPhone: string;
  userEmail: string;
}

export interface FormFieldsProps {
  onSubmit: (data: ContactFormSubmitData) => void;
  mode?: FormFieldMode;
  successfullySent: boolean;
  withSubmitButton?: boolean;
  inModal?: boolean;
  userData?: {
    userPhone: string;
    userName: string;
    userEmail: string;
  };
  handlePhoneClickEvent?: () => void;
}

export const FormFields: React.FC<FormFieldsProps> = (props) => {
  const {
    onSubmit,
    mode = FormFieldMode.Column,
    successfullySent,
    withSubmitButton,
    inModal,
    userData,
    handlePhoneClickEvent,
  } = props;

  const isMobile = useScreenBreakpoint() === 1;
  const { t } = useLocale();
  const [ userName, setUserName ] = useState(userData && userData.userName || '');
  const [ userPhone, setUserPhone ] = useState(userData && userData.userPhone || '');
  const [ userEmail, setUserEmail ] = useState(userData && userData.userEmail || '');
  const [ canSubmit, setCanSubmit ] = useState(false);
  const [ errors, setErrors ] = useState<Record<'name' | 'phone' | 'email', string | boolean>>({
    name: false,
    phone: false,
    email: false,
  });

  const onNameChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => setUserName(e.currentTarget.value), [ userName ]);
  const onPhoneChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => setUserPhone(e.currentTarget.value), [ userPhone ]);
  const onEmailChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => setUserEmail(e.currentTarget.value), [ userEmail ]);

  useEffect(() => setCanSubmit(Boolean(userName && userPhone)), [ userName, userPhone ]);

  const validateAndSubmitForm = () => {
    const emailErr = !userEmail ? false : (isEmailValid(userEmail) ? false : t('mortgage.officePage.contact.wrongEmail'));
    const phoneErr = !userPhone
      ? t('bulletinForm.validationMessage.emptyField')
      : (!isPhoneNumberValid(userPhone) ? t('mortgage.officePage.contact.wrongPhone') : false);
    const nameErr = !Boolean(userName) ? t('bulletinForm.validationMessage.emptyField') : false;

    if (!nameErr && !phoneErr && !emailErr) {
      onSubmit({ userName, userPhone, userEmail });
    }
    else {
      setErrors({ name: nameErr, phone: phoneErr, email: emailErr });
    }
  };

  const handleSubmit = useCallback(() => {
    if (canSubmit) {
      validateAndSubmitForm();
    }
  }, [ canSubmit, userName, userPhone, userEmail, onSubmit ]);

  const successContent = (
    <>
    <Styled.SuccessWrapper inModal={inModal} isRowMode={mode === FormFieldMode.Row}>
      <SuccessIcon />
      <Styled.SuccessMessage weight="bold">{t('officePage.contact.success.message')}</Styled.SuccessMessage>
    </Styled.SuccessWrapper>
    </>
  );

  return (
    <Styled.SectionForm>
      {successfullySent ? (
        successContent
      ) : (
        <Styled.FormWrapper mode={mode} inModal={inModal}>
          <TextField
            fullWidth
            value={userName}
            hasError={Boolean(errors.name)}
            errorMessage={t('bulletinForm.validationMessage.emptyField')}
            onChange={onNameChange}
            placeholder={t('profile.fullNameLabel')}
            icon={<ProfileIcon />}
          />
          <TextField
            fullWidth
            value={userPhone}
            hasError={Boolean(errors.phone)}
            errorMessage={t('bulletinForm.validationMessage.emptyField')}
            onChange={onPhoneChange}
            placeholder={t('contactForm.contact.form.placeholder.phone')}
            icon={<PhoneIconNegative />}
            onClick={handlePhoneClickEvent}
          />
          <TextField
            fullWidth
            value={userEmail}
            hasError={Boolean(errors.email)}
            errorMessage={t('mortgage.officePage.contact.wrongEmail')}
            onChange={onEmailChange}
            placeholder={t('input.olaceholder.email')}
            icon={<EmailIcon />}
          />
          {withSubmitButton ? (
            <Styled.ButtonWrapper inModal={inModal}>
              <Button
                onClick={handleSubmit}
                fullWidth
                size={isMobile && inModal ? 'extraLarge' : 'large'}
                disabled={!canSubmit}
              >
                {t('developerPage.contactModal.submit')}
              </Button>
            </Styled.ButtonWrapper>
          ) : null}
        </Styled.FormWrapper>
      )}
    </Styled.SectionForm>
  );
};
